import { useState, ChangeEvent } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { DESKTOP_PER_PAGE_OPTIONS, MOBILE_PER_PAGE_OPTIONS } from '@constants';

interface UsePaginationProps<T = string> {
  initialLimit?: number;
  initialSkip?: number;
  isMobileScreen?: boolean;
  isLoading?: boolean;
  setSearchFilters: (filters: { limit?: number; skip?: number }) => void;
  removeSearchFilter: (filter: T) => void;
}

interface UsePaginationReturn {
  currentPage: number;
  paginationLimit: number;
  resetPaginationPage: () => void;
  handlePageSizeChange: (event: SelectChangeEvent<unknown>) => void;
  handlePaginationChange: (event: ChangeEvent<unknown>, page: number) => void;
}

export const usePagination = <T extends string = string>({
  initialLimit,
  initialSkip = 0,
  isMobileScreen = false,
  isLoading = false,
  setSearchFilters,
  removeSearchFilter,
}: UsePaginationProps<T>): UsePaginationReturn => {
  const defaultLimit = isMobileScreen ? MOBILE_PER_PAGE_OPTIONS[0] : DESKTOP_PER_PAGE_OPTIONS[0];
  const limitValue = initialLimit || defaultLimit;

  const [currentPage, setCurrentPage] = useState(initialSkip / limitValue + 1);
  const [paginationLimit, setPaginationLimit] = useState(limitValue);

  const resetPaginationPage = () => {
    removeSearchFilter('skip' as T);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<unknown>) => {
    resetPaginationPage();
    setPaginationLimit(event.target.value as number);
    setSearchFilters({ limit: event.target.value as number });
  };

  const handlePaginationChange = (event: ChangeEvent<unknown>, page: number) => {
    if (isLoading) {
      return;
    }

    const skip = (page - 1) * paginationLimit;
    setSearchFilters({ skip });
    setCurrentPage(page);
  };

  return {
    currentPage,
    paginationLimit,
    resetPaginationPage,
    handlePageSizeChange,
    handlePaginationChange,
  };
};
